import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CountryCode } from 'libphonenumber-js';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { StaffUserApiService, StaffUserRow } from 'src/app/core/services/staff-user-api.service';
import { RoleApiService, RoleRow } from 'src/app/core/services/role-api.service';
import { IntlPhoneFieldComponent } from 'src/app/shared/components/intl-phone-field/intl-phone-field.component';
import {
  dialStringForCountry,
  normalizeNationalDigits,
  splitPhoneForForm,
} from 'src/app/shared/utils/international-phone';
import { isAdminUserUuid } from '../admin-user-route.util';

@Component({
  selector: 'app-create-admin-user',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    IntlPhoneFieldComponent,
  ],
  templateUrl: './create-admin-user.component.html',
})
export class CreateAdminUserComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly userApi = inject(StaffUserApiService);
  private readonly roleApi = inject(RoleApiService);
  private readonly snackBar = inject(MatSnackBar);

  userForm!: FormGroup;
  roles: RoleRow[] = [];
  selectedFile: File | null = null;
  selectedFileUrl: string | ArrayBuffer | null = null;
  isSubmitting = false;

  isEditMode = false;
  userUuid: string | null = null;
  userId: number | null = null;

  phoneIso: CountryCode = 'IN';
  phoneNational = '';
  phoneError = '';

  showSuccessModal = false;
  private successRedirectTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.initForm();
    this.loadRoles();
    this.checkEditMode();
  }

  private initForm(): void {
    this.userForm = this.fb.group({
      full_name: ['', [Validators.required]],
      email_id: ['', [Validators.required, Validators.email]],
      role_id: [null, [Validators.required]],
    });
  }

  private checkEditMode(): void {
    const uuidParam = (this.route.snapshot.paramMap.get('uuid') ?? '').trim();
    if (isAdminUserUuid(uuidParam)) {
      this.isEditMode = true;
      this.userUuid = uuidParam;
      this.loadUserDetails(uuidParam);
    }
  }

  private loadUserDetails(uuid: string): void {
    this.userApi.getUserByUuid(uuid).subscribe({
      next: (res) => {
        if (res.status && res.data) {
          const user = res.data;
          this.userId = user.id;
          if (user.uuid?.trim()) {
            this.userUuid = user.uuid.trim();
          }
          this.userForm.patchValue({
            full_name: user.full_name,
            email_id: user.email_id,
            role_id: user.user_roles?.[0]?.role?.id ?? user.user_roles?.[0]?.role_id ?? null,
          });
          const split = splitPhoneForForm(user.country_code, user.mobile_number, 'IN', user.country_code);
          this.phoneIso = split.iso;
          this.phoneNational = split.national;
          this.selectedFileUrl = this.profilePreviewUrl(user);
        }
      },
      error: (err) => {
        this.snackBar.open(
          err?.error?.message || err?.message || 'Could not load user.',
          'Dismiss',
          { duration: 5000 },
        );
      },
    });
  }

  private loadRoles(): void {
    this.roleApi.fetchRoleDropdown().subscribe({
      next: (res) => {
        if (res.status && res.data) {
          this.roles = res.data;
        }
      },
      error: () => {
        this.snackBar.open('Could not load roles.', 'Dismiss', { duration: 5000 });
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.selectedFileUrl = e.target?.result || null;
      };
      reader.readAsDataURL(file);
    }
  }

  removeFile(): void {
    if (this.selectedFile) {
      this.selectedFile = null;
      this.selectedFileUrl = null;
      return;
    }

    if (this.isEditMode && this.userId && this.selectedFileUrl) {
      this.userApi.deleteProfilePicture(this.userId).subscribe({
        next: (res) => {
          if (res.status) {
            this.selectedFile = null;
            this.selectedFileUrl = null;
          }
        },
        error: (err) => {
          this.snackBar.open(
            err?.error?.message || err?.message || 'Could not remove profile picture.',
            'Dismiss',
            { duration: 5000 },
          );
        },
      });
    } else {
      this.selectedFile = null;
      this.selectedFileUrl = null;
    }
  }

  onSubmit(): void {
    this.phoneError = '';

    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      if (!this.phoneNational.trim()) {
        this.phoneError = 'Phone number is required';
      }
      return;
    }

    const mobileDigits = this.normalizeMobileForApi();
    if (!mobileDigits) {
      this.phoneError = 'Phone number is required';
      return;
    }
    if (!/^\d{10}$/.test(mobileDigits)) {
      this.phoneError = 'Mobile number must be exactly 10 digits';
      return;
    }

    this.isSubmitting = true;
    const formValue = this.userForm.value;
    const formData = new FormData();
    formData.append('full_name', formValue.full_name);
    formData.append('email_id', formValue.email_id);
    formData.append('mobile_number', mobileDigits);
    formData.append('country_code', dialStringForCountry(this.phoneIso));
    formData.append('role_id', String(+formValue.role_id));

    if (this.selectedFile) {
      formData.append('profile_picture', this.selectedFile);
    }

    const request =
      this.isEditMode && this.userId
        ? this.userApi.updateUser(this.userId, formData)
        : this.userApi.createUser(formData);

    request.subscribe({
      next: (res) => {
        this.isSubmitting = false;
        if (res.status) {
          this.openSuccessModal();
        } else {
          this.snackBar.open(res?.message || 'Could not save user.', 'Dismiss', { duration: 5000 });
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.snackBar.open(
          err?.error?.message || err?.message || 'Could not save user.',
          'Dismiss',
          { duration: 6000 },
        );
      },
    });
  }

  /** Strip formatting; for India drop a single leading 0 on 11-digit input. */
  private normalizeMobileForApi(): string {
    let digits = normalizeNationalDigits(this.phoneNational);
    if (this.phoneIso === 'IN' && digits.length === 11 && digits.startsWith('0')) {
      digits = digits.slice(1);
    }
    return digits;
  }

  private profilePreviewUrl(user: StaffUserRow): string | null {
    const pp = user.profile_picture;
    if (!pp) return null;
    if (typeof pp === 'string') return pp;
    if (typeof pp === 'object' && pp !== null) {
      const o = pp as Record<string, unknown>;
      if (typeof o['preview_url'] === 'string') return o['preview_url'];
    }
    return null;
  }

  private openSuccessModal(): void {
    this.clearSuccessRedirectTimer();
    this.showSuccessModal = true;
    this.successRedirectTimer = setTimeout(() => {
      this.successRedirectTimer = null;
      this.navigateToUserList();
    }, 2000);
  }

  closeSuccessModal(): void {
    this.clearSuccessRedirectTimer();
    this.showSuccessModal = false;
    this.navigateToUserList();
  }

  private navigateToUserList(): void {
    this.showSuccessModal = false;
    void this.router.navigate(['/manage-admin-users']);
  }

  private clearSuccessRedirectTimer(): void {
    if (this.successRedirectTimer !== null) {
      clearTimeout(this.successRedirectTimer);
      this.successRedirectTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearSuccessRedirectTimer();
  }
}
